import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { cn } from 'lib/utils';
import { useSignedImageRehydrate } from '../hooks';
import {
  upgradeRawLatex,
  MATH_CLASS,
  MATH_DISPLAY_CLASS,
  MATH_AUTO_CLASS,
} from '../rawLatex';

// Defense-in-depth: the API sanitizes on write (see
// apps/api/src/common/sanitize-rich-html.ts), but this renderer writes
// straight into `innerHTML` to coexist with KaTeX — a single missed
// sanitization on any new write path would execute here. Sanitizing again
// at render closes that gap and also covers pre-sanitizer data already in
// the DB.
const PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'p', 'br', 'h2', 'h3',
    'strong', 'em', 'u', 's', 'code', 'pre', 'mark', 'blockquote',
    'ul', 'ol', 'li',
    'a', 'img',
    'span', 'div',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ],
  ALLOWED_ATTR: [
    'href', 'rel', 'target',
    'src', 'alt', 'title', 'data-b2-key', 'width', 'height',
    'class', 'data-latex', 'data-checked', 'data-type',
    'colspan', 'rowspan', 'colwidth',
  ],
  ADD_URI_SAFE_ATTR: ['width', 'height'],
  ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):/i,
  FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form'],
  FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
};

const CODE_SCROLL_CLASS = '[&_pre]:max-w-full [&_pre]:overflow-x-auto';

interface RenderedHtmlProps {
  html: string;
  className?: string;
  inline?: boolean;
}

/** DOMPurify, then innerHTML via a ref (not dangerouslySetInnerHTML), then KaTeX over `.math-tex`. */
const RenderedHtml: React.FC<RenderedHtmlProps> = ({ html, className, inline }) => {
  const ref = useRef<HTMLDivElement | HTMLSpanElement | null>(null);
  const content = inline ? stripOuterParagraph(html) : html;

  /** dangerouslySetInnerHTML lets React rewrite inner DOM and wipe KaTeX's output; owning innerHTML avoids that. */
  useEffect(() => {
    if (!ref.current) return;
    const root = ref.current;

    // Sanitize before assigning to innerHTML. See PURIFY_CONFIG above for
    // why this is a defense-in-depth pass even though the API also sanitizes.
    const safe = content ? DOMPurify.sanitize(content, PURIFY_CONFIG) : '';

    // Only rewrite innerHTML if the source content actually differs. Avoids
    // a flicker where React-driven re-renders briefly show raw HTML before
    // KaTeX re-runs.
    if (root.innerHTML !== safe) {
      root.innerHTML = safe;
    }

    // AI-generated questions arrive as plain text with raw `\( … \)` delimiters
    // rather than editor markup. Upgrading them first means one KaTeX pass
    // covers both sources. Synchronous, so useSignedImageRehydrate below sees
    // the final DOM.
    upgradeRawLatex(root);

    const nodes = root.querySelectorAll<HTMLElement>(`.${MATH_CLASS}`);
    if (!nodes.length) return;

    let cancelled = false;
    (async () => {
      const katex = (await import('katex')).default;
      // KaTeX CSS is already in apps/web/src/index.css.

      if (cancelled || !ref.current) return;
      nodes.forEach((el) => {
        const latex = el.getAttribute('data-latex') || el.textContent || '';
        // Heuristically-detected math must parse cleanly or not at all: a
        // lenient parse would render KaTeX's red error text over something that
        // was probably never an expression. Delimited/authored math stays
        // lenient, since there the intent to typeset is explicit.
        const isAuto = el.classList.contains(MATH_AUTO_CLASS);
        // `katex.render` empties the node BEFORE it parses, so a throw leaves it
        // blank rather than untouched. Keep the source to put back.
        const source = el.textContent ?? '';
        try {
          katex.render(latex.replace(/^\\\(|\\\)$/g, ''), el, {
            throwOnError: isAuto,
            displayMode: el.classList.contains(MATH_DISPLAY_CLASS),
          });
        } catch {
          el.textContent = source;
        }
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [content]);

  // Refresh signed URLs for any <img data-b2-key>. The bucket is Private so
  // src is short-lived; this hook batches keys, calls /api/uploads/sign, and
  // swaps src in place. Declared AFTER the innerHTML-writing effect so React
  // runs them in that order — the rehydrator sees the freshly-written DOM.
  useSignedImageRehydrate(ref, content);

  if (inline) {
    return (
      <span
        ref={ref as React.RefObject<HTMLSpanElement>}
        className={cn('inline', CODE_SCROLL_CLASS, className)}
        // Intentionally NOT using dangerouslySetInnerHTML — see effect above.
      />
    );
  }

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert',
        'prose-table:border prose-table:border-gray-300',
        'prose-td:border prose-td:border-gray-300 prose-td:px-2 prose-td:py-1',
        'prose-th:border prose-th:border-gray-300 prose-th:bg-gray-100 prose-th:px-2 prose-th:py-1',
        CODE_SCROLL_CLASS,
        className,
      )}
      // Intentionally NOT using dangerouslySetInnerHTML — see effect above.
    />
  );
};

/** Lets authored content drop into a span without nesting a paragraph inside it. */
function stripOuterParagraph(html: string): string {
  const trimmed = html?.trim() ?? '';
  const match = trimmed.match(/^<p[^>]*>([\s\S]*?)<\/p>$/i);
  if (!match) return trimmed;
  // Only strip if there's no additional top-level <p> tag after this one
  if (/<\/p>\s*<p[^>]*>/i.test(trimmed)) return trimmed;
  return match[1];
}

export default RenderedHtml;
